/**
 * WizardStepper — Unified step indicator for all multi-step modals.
 * Displays numbered circles connected by lines.
 * Completed steps show a checkmark, current step has a ring accent.
 *
 * Props:
 *   totalSteps  — number of steps (e.g. 3, 5)
 *   currentStep — 1-based index of the active step
 *   labels      — optional string[] of step labels (shown below circles on sm+)
 */
import { CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export const WizardStepper = ({ totalSteps, currentStep, labels }) => (
  <div className="flex items-start justify-center gap-0 my-3" data-testid="wizard-stepper">
    {Array.from({ length: totalSteps }, (_, i) => {
      const stepNum = i + 1;
      const isCompleted = stepNum < currentStep;
      const isCurrent = stepNum === currentStep;

      return (
        <div key={stepNum} className="flex items-start">
          {/* Circle + label column */}
          <div className="flex flex-col items-center w-[72px]">
            <div
              className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors duration-200 shrink-0",
                isCurrent && "ring-2 ring-offset-1 ring-[var(--primary-400)]",
                (isCompleted || isCurrent)
                  ? "bg-[var(--primary-400)] text-white"
                  : "bg-[var(--neutral-200)] text-[var(--text-secondary)]",
              )}
              data-testid={`wizard-step-circle-${stepNum}`}
            >
              {isCompleted ? <CheckCircle className="h-3.5 w-3.5" /> : stepNum}
            </div>
            {/* Label (visible sm+) */}
            {labels && labels[i] && (
              <span
                className={cn(
                  "hidden sm:block text-[10px] mt-1.5 text-center leading-tight",
                  (isCompleted || isCurrent)
                    ? "font-semibold text-[var(--primary-600)]"
                    : "text-[var(--text-muted)]",
                )}
              >
                {labels[i]}
              </span>
            )}
          </div>

          {/* Connector line — aligned to the circle center */}
          {stepNum < totalSteps && (
            <div
              className={cn(
                "h-0.5 w-6 mt-[13px] -mx-2 transition-colors duration-200",
                stepNum < currentStep ? "bg-[var(--primary-400)]" : "bg-[var(--neutral-200)]",
              )}
            />
          )}
        </div>
      );
    })}
  </div>
);
